import React, { useState, useEffect } from 'react';
import './App.css';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SalesProvider } from './context/SalesContext';
import { ProductsProvider, useProducts } from './context/ProductsContext';
import MainNavigation from './components/MainNavigation';
import HomePage from './components/HomePage';
import ProductsPage from './components/ProductsPage';
import FarmHistory from './components/FarmHistory';
import Footer from './components/Footer';
import AdminPanel from './components/AdminPanel';
import MyAdmin from './components/MyAdmin';
import Cart from './components/Cart';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import ProductSync from './components/ProductSync';
import ProductSyncTest from './components/ProductSyncTest';

function AppContent() {
  const [showAdmin, setShowAdmin] = useState(false);
  const [showMyAdmin, setShowMyAdmin] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState('accueil');
  const { isAdmin } = useAuth();
  const { clearCart } = useCart();
  const { updateProduct, products } = useProducts();

  // Gérer le retour de Stripe après un paiement réussi
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const sessionId = urlParams.get('session_id');
      const canceled = urlParams.get('canceled');

      if (success === 'true' && sessionId) {
        console.log('✅ Paiement Stripe réussi, session_id:', sessionId);
        
        // Réduire le stock des produits achetés sur le serveur (synchronisé pour tous)
        const purchasedItems = sessionStorage.getItem('stripe-purchase-items');
        if (purchasedItems) {
          try {
            const items = JSON.parse(purchasedItems);
            console.log('📦 Réduction du stock pour', items.length, 'produit(s) sur le serveur...');
            
            // Importer stripeService
            const stripeService = (await import('./services/stripeService')).default;
            
            // Mettre à jour le stock sur le serveur
            await stripeService.updateStock(items);
            
            // Récupérer le stock à jour depuis le serveur
            const serverStock = await stripeService.getProductsStock();
            
            // Mettre à jour les produits localement avec le stock du serveur
            if (serverStock && Object.keys(serverStock).length > 0) {
              items.forEach(item => {
                const serverProductStock = serverStock[item.id];
                if (serverProductStock !== undefined) {
                  const product = products.find(p => p.id === item.id);
                  if (product) {
                    updateProduct(item.id, { stock: serverProductStock.stock });
                    console.log(`📉 Stock mis à jour: ${product.name} → ${serverProductStock.stock}`);
                  }
                }
              });
            }
            
            // Nettoyer les items sauvegardés
            sessionStorage.removeItem('stripe-purchase-items');
            console.log('✅ Stock réduit avec succès sur le serveur');
          } catch (error) {
            console.error('❌ Erreur lors de la réduction du stock:', error);
            // En cas d'erreur, on réduit quand même localement comme fallback
            try {
              const items = JSON.parse(purchasedItems);
              items.forEach(item => {
                const product = products.find(p => p.id === item.id);
                if (product) {
                  const newStock = Math.max(0, (product.stock || 0) - item.quantity);
                  updateProduct(item.id, { stock: newStock });
                }
              });
            } catch (fallbackError) {
              console.error('❌ Erreur lors de la réduction locale de fallback:', fallbackError);
            }
          }
        }
        
        // Vider le panier seulement si le paiement a réussi
        clearCart();
      
        // Restaurer la page où l'utilisateur était avant le paiement
        const savedPage = sessionStorage.getItem('stripe-return-page');
        if (savedPage) {
          sessionStorage.removeItem('stripe-return-page');
          const page = savedPage.substring(1) || 'accueil'; // Enlever le '/' du début
          console.log('🔄 Retour à la page précédente:', page);
          
          // Changer la page
          setCurrentPage(page);
          
          // Mettre à jour l'URL
          const newUrl = page === 'accueil' ? '/' : `/${page}`;
          window.history.replaceState({ page }, '', newUrl);
        } else {
          // Si pas de page sauvegardée, nettoyer juste l'URL
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
        }
        
        // Afficher un message de succès (optionnel, peut être amélioré avec une notification)
        console.log('🎉 Merci pour votre achat !');
      } else if (canceled === 'true') {
        console.log('❌ Paiement annulé par l\'utilisateur');
        // Ne pas vider le panier si le paiement a été annulé
        
        // Restaurer la page où l'utilisateur était avant le paiement
        const savedPage = sessionStorage.getItem('stripe-return-page');
        if (savedPage) {
          sessionStorage.removeItem('stripe-return-page');
          const page = savedPage.substring(1) || 'accueil'; // Enlever le '/' du début
          console.log('🔄 Retour à la page précédente (paiement annulé):', page);
          
          // Changer la page
          setCurrentPage(page);
          
          // Mettre à jour l'URL
          const newUrl = page === 'accueil' ? '/' : `/${page}`;
          window.history.replaceState({ page }, '', newUrl);
        } else {
          // Si pas de page sauvegardée, nettoyer juste l'URL
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
        }
      }
    };
    
    handlePaymentSuccess();
  }, [clearCart, setCurrentPage, updateProduct, products]);

  // Remonter en haut de la page lors du changement de page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // Gestion de l'historique du navigateur
  useEffect(() => {
    // Fonction pour gérer les changements d'URL
    const handlePopState = (event) => {
      const url = window.location.pathname;
      const page = url.substring(1) || 'accueil'; // Enlever le '/' du début
      setCurrentPage(page);
    };

    // Écouter les changements d'URL (bouton retour du navigateur)
    window.addEventListener('popstate', handlePopState);

    // Initialiser la page depuis l'URL actuelle
    const url = window.location.pathname;
    const page = url.substring(1) || 'accueil';
    setCurrentPage(page);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Fonction pour changer de page avec mise à jour de l'URL
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Mettre à jour l'URL dans l'historique du navigateur
    const newUrl = page === 'accueil' ? '/' : `/${page}`;
    window.history.pushState({ page }, '', newUrl);
  };

  // Fonction pour basculer vers l'admin (normalement avec authentification)
  const toggleAdmin = () => {
    setShowAdmin(!showAdmin);
    setShowMyAdmin(false);
  };

  // Fonction pour basculer vers MyAdmin
  const toggleMyAdmin = () => {
    setShowMyAdmin(!showMyAdmin);
    setShowAdmin(false);
  };

  // Fonctions pour gérer l'ouverture/fermeture du panier
  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  // Fonction pour retourner à la page d'accueil depuis MyAdmin
  const backToHome = () => {
    setShowMyAdmin(false);
    setShowAdmin(false);
  };


  // Fonction pour gérer le clic sur un produit ou la navigation
  const handleProductClick = (product) => {
    if (product === 'all') {
      handlePageChange('produits');
    } else if (product === 'histoire') {
      handlePageChange('histoire');
    } else {
      // Ici on pourrait ouvrir une modal ou rediriger vers une page produit spécifique
      console.log('Produit sélectionné:', product);
    }
  };

  // Si on est en mode MyAdmin, afficher React-Admin avec protection
  if (showMyAdmin) {
    return (
      <AdminProtectedRoute>
        <MyAdmin onBackToHome={backToHome} />
      </AdminProtectedRoute>
    );
  }

  // Si on est en mode admin, afficher seulement le panel admin avec protection
  if (showAdmin) {
    return (
      <AdminProtectedRoute>
        <div className="App">
          <div style={{ 
            position: 'fixed', 
            top: '20px', 
            right: '20px', 
            zIndex: 1001 
          }}>
            <button 
              onClick={toggleAdmin}
              style={{
                background: 'linear-gradient(135deg, #dc3545, #c82333)',
                color: '#e8f5e8',
                border: '1px solid rgba(26, 61, 10, 0.3)',
                padding: '0.5rem 1rem',
                borderRadius: '5px',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 15px rgba(26, 61, 10, 0.3)'
              }}
            >
              Retour au Site
            </button>
          </div>
          <AdminPanel />
        </div>
      </AdminProtectedRoute>
    );
  }

  // Fonction pour rendre le contenu de la page
  const renderPageContent = () => {
    switch (currentPage) {
      case 'accueil':
        return <HomePage onProductClick={handleProductClick} />;
      case 'produits':
        return <ProductsPage />;
      case 'histoire':
        return <FarmHistory />;
      default:
        return <HomePage onProductClick={handleProductClick} />;
    }
  };

  return (
    <div className="App">
      <ProductSync />
      <ProductSyncTest />
      <MainNavigation 
        onCartOpen={openCart}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
      
      {/* Boutons d'accès admin (seulement pour les admins connectés) */}
      {isAdmin && (
        <div style={{ 
          position: 'fixed', 
          top: '100px', 
          right: '20px', 
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          <button 
            onClick={toggleMyAdmin}
            style={{
              background: 'linear-gradient(135deg, #4a7c59, #2d5016)',
              color: '#e8f5e8',
              border: '1px solid rgba(26, 61, 10, 0.3)',
              padding: '0.5rem 1rem',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(26, 61, 10, 0.3)'
            }}
          >
            MyAdmin
          </button>
          <button 
            onClick={toggleAdmin}
            style={{
              background: 'linear-gradient(135deg, #2d5016, #1a3d0a)',
              color: '#e8f5e8',
              border: '1px solid rgba(26, 61, 10, 0.3)',
              padding: '0.5rem 1rem',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(26, 61, 10, 0.3)'
            }}
          >
            Admin Simple
          </button>
        </div>
      )}

      {renderPageContent()}

      <Footer onPageChange={handlePageChange} />
      
      {/* Interface Panier */}
      <Cart isOpen={isCartOpen} onClose={closeCart} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProductsProvider>
        <CartProvider>
          <SalesProvider>
            <AppContent />
          </SalesProvider>
        </CartProvider>
      </ProductsProvider>
    </AuthProvider>
  );
}

export default App;
